import { useState } from 'react';
import { Badge } from './Badge';
import { DocumentList } from './DocumentList';
import { CAL_COLORS, RESERVATION_COLORS, initials } from '../lib/format';
import type {
  EnrichedResource,
  NewReservation,
  Reservation,
} from '../lib/types';

/**
 * Resource detail: calibration state, every booking on this resource,
 * and an inline form to add another. Bookings are matched on the
 * resource's title, which is the key the two tables share.
 */
export function ResourceModal({
  resource,
  reservations,
  defaultReservedBy,
  saving,
  onAdd,
  canSchedule,
  onCancel,
  onEditReservation,
  onEdit,
  onClose,
}: {
  resource: EnrichedResource;
  reservations: Reservation[];
  defaultReservedBy: string;
  saving: boolean;
  onAdd: (reservation: NewReservation) => void;
  /** Viewer-tier users see the bookings but get no Add / Edit / Cancel. */
  canSchedule: boolean;
  onCancel: (reservation: Reservation) => void;
  /** Edit one of the bookings listed below — dates, project, status. */
  onEditReservation: (reservation: Reservation) => void;
  /** Edit the resource itself. Admin-only; omitted for everyone else. */
  onEdit?: () => void;
  onClose: () => void;
}) {
  const isPerson = resource.type === 'Personnel';
  // Mirrors the server's rule, so the form isn't offered for a booking
  // the API would refuse.
  const bookable = resource.isActive && resource.status === 'Available';
  const items = reservations
    .filter((r) => r.equipment === resource.title)
    .sort((a, b) => a.startDate.localeCompare(b.startDate));

  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState<NewReservation>({
    equipment: resource.title,
    projectNumber: '',
    projectName: '',
    startDate: '',
    endDate: '',
    reservedBy: defaultReservedBy,
    notes: '',
    status: 'Confirmed',
  });
  const update = <K extends keyof NewReservation>(
    key: K,
    value: NewReservation[K],
  ) => setForm((prev) => ({ ...prev, [key]: value }));

  const ready =
    form.projectNumber.trim() !== '' &&
    form.startDate !== '' &&
    form.endDate !== '' &&
    form.reservedBy.trim() !== '';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {isPerson && (
              <div className="avatar avatar-lg">{initials(resource.name)}</div>
            )}
            <div>
              <div
                style={{
                  fontSize: 15,
                  fontWeight: 500,
                  color: 'var(--text-primary)',
                }}
              >
                {resource.name}
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: 'var(--text-secondary)',
                  marginTop: 2,
                }}
              >
                {resource.category}
                {resource.office ? ` · ${resource.office}` : ''}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {onEdit && (
              <button
                onClick={onEdit}
                style={{
                  fontSize: 11,
                  padding: '3px 9px',
                  borderRadius: 6,
                  background: 'var(--bg-secondary)',
                  color: 'var(--text-secondary)',
                  border: '0.5px solid var(--border-md)',
                }}
              >
                Edit
              </button>
            )}
            <button className="modal-close" onClick={onClose}>
              ×
            </button>
          </div>
        </div>

        {!isPerson && resource.cal && (
          <div className="modal-meta">
            <div>
              <div className="modal-meta-label">Calibration status</div>
              <Badge label={resource.cal} colors={CAL_COLORS} />
            </div>
            <div>
              <div className="modal-meta-label">Next due</div>
              <div
                className="modal-meta-value"
                style={{
                  color: resource.cal === 'Overdue' ? '#a32d2d' : undefined,
                }}
              >
                {resource.nextDue || '—'}
              </div>
            </div>
          </div>
        )}

        {/* Equipment only: personnel have no certificates or manuals,
            and an empty Documents section on every person is noise. */}
        {!isPerson && (
          <DocumentList equipmentId={resource.id} canUpload={canSchedule} />
        )}

        <div className="section-title" style={{ marginTop: 14 }}>
          Reservations
        </div>
        {items.length === 0 && (
          <div
            style={{
              fontSize: 13,
              color: 'var(--text-tertiary)',
              marginBottom: 10,
            }}
          >
            No reservations on file.
          </div>
        )}
        {items.map((r) => (
          <div key={r.id} className="res-item">
            <div className="res-item-header">
              <span className="res-item-name">
                {r.projectNumber} – {r.projectName}
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {/* Edit sits on the far side of the badge from Cancel.
                    Adjacent, the two were a mis-click apart, and one of
                    them ends a booking. The badge is the separator. */}
                {r.status !== 'Canceled' && canSchedule && (
                  <button
                    onClick={() => onEditReservation(r)}
                    style={{
                      fontSize: 11,
                      padding: '2px 7px',
                      borderRadius: 6,
                      background: 'var(--bg-secondary)',
                      color: 'var(--text-secondary)',
                      border: '0.5px solid var(--border-md)',
                    }}
                  >
                    Edit
                  </button>
                )}
                <Badge label={r.status} colors={RESERVATION_COLORS} />
                {r.status !== 'Canceled' && canSchedule && (
                  <button
                    onClick={() => onCancel(r)}
                    style={{
                      fontSize: 11,
                      padding: '2px 7px',
                      borderRadius: 6,
                      background: 'var(--red-bg)',
                      color: 'var(--red-fg)',
                      border: '0.5px solid var(--red-bd)',
                      marginLeft: 4,
                    }}
                  >
                    Cancel
                  </button>
                )}
              </div>
            </div>
            <div className="res-item-sub">
              {r.reservedBy} · {r.startDate} → {r.endDate}
            </div>
            {r.notes && (
              <div
                style={{
                  fontSize: 12,
                  color: 'var(--text-tertiary)',
                  marginTop: 3,
                }}
              >
                {r.notes}
              </div>
            )}
            {r.canceledBy && (
              <div style={{ fontSize: 12, color: '#a32d2d', marginTop: 3 }}>
                Canceled by {r.canceledBy}: {r.cancellationReason}
              </div>
            )}
          </div>
        ))}

        {/* A viewer gets the bookings but no way to add one. The
            not-bookable note is still worth showing them: it explains
            the resource's state, which is information rather than an
            action they're being denied. */}
        {!bookable ? (
          <div
            style={{
              fontSize: 12,
              color: 'var(--text-tertiary)',
              marginTop: 6,
            }}
          >
            {resource.isActive
              ? `Not bookable while its status is “${resource.status}”.`
              : 'Retired — not bookable. Existing bookings are kept.'}
          </div>
        ) : !canSchedule ? null : !adding ? (
          <button className="add-res-btn" onClick={() => setAdding(true)}>
            + Add reservation
          </button>
        ) : (
          <div className="modal-form">
            <div className="section-title">New reservation</div>
            <div className="modal-form-grid">
              <input
                placeholder="Project number"
                value={form.projectNumber}
                onChange={(e) => update('projectNumber', e.target.value)}
              />
              <input
                placeholder="Project name"
                value={form.projectName}
                onChange={(e) => update('projectName', e.target.value)}
              />
              <input
                placeholder="Reserved by"
                value={form.reservedBy}
                onChange={(e) => update('reservedBy', e.target.value)}
              />
              <select
                value={form.status}
                onChange={(e) =>
                  update('status', e.target.value as NewReservation['status'])
                }
              >
                <option>Confirmed</option>
                <option>Pending</option>
                <option>Active</option>
              </select>
              <input
                type="date"
                value={form.startDate}
                onChange={(e) => update('startDate', e.target.value)}
              />
              <input
                type="date"
                value={form.endDate}
                onChange={(e) => update('endDate', e.target.value)}
              />
            </div>
            <input
              placeholder="Notes (optional)"
              value={form.notes}
              onChange={(e) => update('notes', e.target.value)}
            />
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                className="btn-save"
                disabled={saving || !ready}
                onClick={() => {
                  onAdd({ ...form, equipment: resource.title });
                  setAdding(false);
                }}
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button className="btn-cancel" onClick={() => setAdding(false)}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
